import HistoryAction from '../actions/HistoryAction';

export interface HistoryState<Snapshot> {
  readonly pastRecords: ReadonlyArray<Snapshot>;
  readonly futureRecords: ReadonlyArray<Snapshot>;
}

export function initHistory() {
  return {
    pastRecords: [],
    futureRecords: [],
  };
}

export function canUndo<Snapshot>(state: HistoryState<Snapshot>) {
  return state.pastRecords.length > 0;
}

export function canRedo<Snapshot>(state: HistoryState<Snapshot>) {
  return state.futureRecords.length > 0;
}

export function prevRecord<Snapshot>(state: HistoryState<Snapshot>) {
  const { pastRecords } = state;
  if (pastRecords.length === 0) {
    throw new Error('Illegal state.');
  }

  return pastRecords[pastRecords.length - 1];
}

export function nextRecord<Snapshot>(state: HistoryState<Snapshot>) {
  const { futureRecords } = state;
  if (futureRecords.length === 0) {
    throw new Error('Illegal state.');
  }

  return futureRecords[futureRecords.length - 1];
}

export function recordForAction<Snapshot>(
  state: HistoryState<Snapshot>,
  action: HistoryAction.UndoAction | HistoryAction.RedoAction,
): Snapshot {
  switch (action.type) {
    case HistoryAction.ActionType.UNDO:
      return prevRecord(state);

    case HistoryAction.ActionType.REDO:
      return nextRecord(state);
  }
}

export function takeSnapshot<Snapshot>(
  state: HistoryState<Snapshot>,
  snapshot: Snapshot,
  maxHistoryRecords: number,
): HistoryState<Snapshot> {
  const pastRecords = state.pastRecords.slice();
  pastRecords.push(snapshot);

  if (pastRecords.length > maxHistoryRecords) {
    pastRecords.splice(0, pastRecords.length - maxHistoryRecords);
  }

  return { pastRecords, futureRecords: [] };
}

export function forEachSnapshot<Snapshot>(
  state: HistoryState<Snapshot>,
  proc: (item: Snapshot) => void,
) {
  for (const snapshot of state.pastRecords) {
    proc(snapshot);
  }

  for (const snapshot of state.futureRecords) {
    proc(snapshot);
  }
}

export default function history<Snapshot>(
  state: HistoryState<Snapshot>,
  currentData: Snapshot,
  action: HistoryAction.Action,
): HistoryState<Snapshot> {
  switch (action.type) {
    case HistoryAction.ActionType.UNDO: {
      const pastRecords = state.pastRecords.slice();
      const futureRecords = state.futureRecords.slice();
      if (pastRecords.length === 0) {
        throw new Error('Illegal state.');
      }

      pastRecords.pop();
      futureRecords.push(currentData);
      return { pastRecords, futureRecords };
    }

    case HistoryAction.ActionType.REDO: {
      const pastRecords = state.pastRecords.slice();
      const futureRecords = state.futureRecords.slice();
      if (futureRecords.length === 0) {
        throw new Error('Illegal state.');
      }

      futureRecords.pop();
      pastRecords.push(currentData);
      return { pastRecords, futureRecords };
    }
  }
}
